import { BarChart3, Calendar, Shield, Activity, Tag } from 'lucide-react';
import type { ReleasePillarDetails } from '../../../types/analysis.types';
import { MetricCard, SectionHeader, DistributionBar, RiskImplicationsPanel } from './shared';
import { safeNum, type PillarDetailsCommonProps } from './shared.utils';

interface Props extends PillarDetailsCommonProps {
  details: ReleasePillarDetails;
}

export const ReleaseDetails: React.FC<Props> = ({
  details,
  metricBg,
  trackColor,
  theme,
  score,
}) => {
  const d = details as unknown as Record<string, unknown>;

  const releases12m = safeNum(d.releaseCount12m ?? d.releasesLastYear ?? d.releases_last_year) ?? 0;
  const daysSince = safeNum(d.daysSinceLastRelease ?? d.days_since_last_release);
  const frequency = safeNum(d.releaseFrequencyDays ?? d.release_frequency_days);
  const hasSemver = Boolean(d.hasSemanticVersioning ?? d.has_semantic_versioning ?? d.hasSemver);
  const hasChangelog = Boolean(d.hasChangelog ?? d.has_changelog);

  const practices = [
    { label: 'Has Releases', present: releases12m > 0 },
    { label: 'Recent Release', present: daysSince !== null && daysSince < 180 },
    { label: 'Semantic Versioning', present: hasSemver },
    { label: 'Changelog', present: hasChangelog },
  ];

  const getRiskMessage = () => {
    if (releases12m === 0)
      return 'No releases in the past year indicates the project may be dormant.';
    if (daysSince !== null && daysSince > 365) return 'Over a year since the last release.';
    if (!hasSemver) return 'Without semantic versioning, users cannot easily assess upgrade risks.';
    return null;
  };

  const riskMessage = getRiskMessage();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <MetricCard
          label="Releases (12m)"
          value={releases12m}
          icon={<Tag className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        {daysSince !== null && daysSince < 9999 && (
          <MetricCard
            label="Last Release"
            value={daysSince}
            subtext="days ago"
            icon={<Calendar className="w-4 h-4 text-secondary" />}
            metricBg={metricBg}
          />
        )}
        {frequency !== null && (
          <MetricCard
            label="Frequency"
            value={`~${frequency}`}
            subtext="days between releases"
            icon={<Activity className="w-4 h-4 text-secondary" />}
            metricBg={metricBg}
          />
        )}
      </div>

      <div>
        <SectionHeader
          icon={<BarChart3 className="w-4 h-4" />}
          title="Release Practices Distribution"
        />
        <div className="space-y-2">
          {practices.map((practice) => (
            <DistributionBar
              key={practice.label}
              label={practice.label}
              value={practice.present ? 1 : 0}
              total={1}
              trackColor={trackColor}
              barColor={practice.present ? '#10b981' : trackColor}
            />
          ))}
        </div>
      </div>

      {riskMessage && score < 70 && (
        <div>
          <SectionHeader icon={<Shield className="w-4 h-4" />} title="Risk Implications" />
          <RiskImplicationsPanel message={riskMessage} theme={theme} />
        </div>
      )}
    </div>
  );
};
